/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect, useRef } from "react";

import { Log } from "../../viewModel/logging";
import { CAROUSEL_TILE_PREFETCH_THRESHOLD } from "../../viewModel/homescreen/homescreenConstants";
import { canLoadMoreOffset } from "../../viewModel/utility/carouselPagination";
import type { TTilePaginationState } from "../../viewModel/utility/carouselPagination";

const TAG: string = "useCarouselTilePrefetch";

/**
 * Encapsulates tile prefetch logic shared between carousel components.
 * Calls onPrefetch when the focused tile is within CAROUSEL_TILE_PREFETCH_THRESHOLD
 * of the end of the loaded tile list and more tiles can be loaded.
 * Debounced (150ms) to avoid rapid network calls during fast navigation.
 * Does not prefetch on initial mount — only after the user has navigated.
 */
export function useCarouselTilePrefetch(params: {
  focusedCarouselId: string | undefined;
  focusedCarouselIndex: number;
  focusedTileIndices: Partial<Record<number, number>>;
  focusedTilePagination: TTilePaginationState | undefined;
  onPrefetch: (carouselId: string) => void;
}): void {
  const {
    focusedCarouselId,
    focusedCarouselIndex,
    focusedTileIndices,
    focusedTilePagination,
    onPrefetch
  } = params;

  const hasUserNavigatedTiles = useRef<boolean>(false);
  const prevFocusedTileIndex = useRef<number>(0);

  useEffect(() => {
    const currentTileIndex: number =
      focusedTileIndices[focusedCarouselIndex] || 0;
    if (prevFocusedTileIndex.current !== currentTileIndex) {
      hasUserNavigatedTiles.current = true;
      prevFocusedTileIndex.current = currentTileIndex;
    }
  }, [focusedCarouselIndex, focusedTileIndices]);

  useEffect(() => {
    if (!hasUserNavigatedTiles.current) {
      return;
    }

    if (
      focusedCarouselId === undefined ||
      focusedTilePagination === undefined
    ) {
      return;
    }

    const focusedTileIndex: number =
      focusedTileIndices[focusedCarouselIndex] || 0;
    const totalTiles: number = focusedTilePagination.items.length;
    const distanceFromEnd: number = totalTiles - focusedTileIndex - 1;

    if (
      !canLoadMoreOffset(focusedTilePagination) ||
      distanceFromEnd > CAROUSEL_TILE_PREFETCH_THRESHOLD
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      Log.i(
        TAG,
        `Prefetching more tiles for carousel=${focusedCarouselId}, focusedTile=${focusedTileIndex}, total=${totalTiles}`
      );
      onPrefetch(focusedCarouselId);
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [
    focusedCarouselId,
    focusedCarouselIndex,
    focusedTileIndices,
    focusedTilePagination,
    onPrefetch
  ]);
}
